import { BehaviorSubject, combineLatest } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { cloneDeep, filter, slice } from 'lodash';
import { take } from 'rxjs/operators';
import { UserService } from '../user.service';
import { NoteService } from '../note.service';
import { EquipmentActivityService } from '../equipmentActivity.service';
import { EquipmentActivity } from '../../_models/equipmentActivity';
import { FarmService } from '../farm.service';
import { Employee } from '../../_models/employee';
import { EmployeeService } from '../employee.service';
import { Crop } from '../../_models/crop';
import { CropService } from '../crop.service';
import { Equipment } from '../../_models/equipment';
import { EquipmentService } from '../equipment.service';
import { FieldService } from '../field.service';
import { Farm, FieldActivity, Note, User } from '../../_models';
import { Field } from '../../_models/field';
import { environment } from '../../../../environments/environment';
import { StaticMapProxyService } from '../static-map-proxy.service';
import { XYearService } from '../xYear.service';
import { FieldActivityService } from '../fieldActivity.service';

@Injectable()
export class OperationsManagementService {
  private apiBaseUrl = environment.apiBaseUrl;
  public multiFieldActivityList$ = new BehaviorSubject<FieldActivity[]>(null);
  public multiFieldList$ = new BehaviorSubject<Field[]>([]);
  public multiEquipmentList$ = new BehaviorSubject<Equipment[]>([]);
  public multiEquipmentActivityList$ = new BehaviorSubject<EquipmentActivity[]>([]);
  public multiCropList$ = new BehaviorSubject<Crop[]>([]);
  public multiEmployeeList$ = new BehaviorSubject<Employee[]>([]);
  public multiNoteList$ = new BehaviorSubject<Note[]>([]);
  private employeeSelected: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private farmSelected: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private dateSelected: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  // Services
  private staticMapsProxyService = inject(StaticMapProxyService);

  constructor(
    private fieldService: FieldService,
    private equipmentService: EquipmentService,
    private fieldActivityService: FieldActivityService,
    private cropService: CropService,
    private employeeService: EmployeeService,
    private farmService: FarmService,
    private xYearService: XYearService,
    private equipmentActivityService: EquipmentActivityService,
    private noteService: NoteService,
    private userService: UserService
  ) {}

  getAllDataToOPModule = () => {
    combineLatest([
      this.xYearService.selectedYear$,
      this.farmService.list,
      this.userService.getAllMultiFarmUsers(),
    ]).subscribe(([selectedYear, farmList, userList]) => {
      if (selectedYear && farmList && farmList.length > 0) {
        // tslint:disable-next-line:prefer-const
        let index = 0;
        let farmsAppend = '';
        farmList = slice(farmList, 0, 100);
        farmList.forEach((item) => {
          if (index === 0) {
            farmsAppend += '?companyIds=' + item.id;
          } else {
            farmsAppend += '&companyIds=' + item.id;
          }
          index++;
        });

        // tslint:disable-next-line:prefer-const
        const excludesList = [
          'plantsPlantingDate',
          'mineralNitrogen',
          'listedInCAP',
          'country',
          'dateCreated',
          'lastUpdated',
          'plantsNote',
          'sizeUnit',
          'landRegistries',
          'fieldActivities',
          'city',
          'plantsX',
          'soilUrl',
          'granulometry',
          'limescale',
          'plantsY',
        ];
        let excludesAppend = '';
        excludesList.forEach((item) => {
          excludesAppend += '&excludes=' + item;
        });
        this.fieldService.getFieldsOPModule(farmsAppend, excludesAppend).subscribe((fieldList) => {
          this.getFarmSelected().subscribe((farmIds) => {
            let responseFiltered = cloneDeep(fieldList);
            if (farmIds?.length) {
              responseFiltered = filter(responseFiltered, (item) =>
                farmIds.includes(item.companyId)
              );
            }
            responseFiltered.forEach((field: Field, index) => {
              this.farmService.setFarmParamByID(field.companyId, 'name');
              field.companyName = this.farmService.getFarmByParam();
              responseFiltered[index].area = JSON.parse(field.geoJsonArea);
              responseFiltered[index].staticMapUrl = this.staticMapsProxyService.getStaticMapUrl(
                JSON.parse(field.geoJsonArea).features
              );
            });
            this.multiFieldList$.next(responseFiltered);
          });
        });
        this.equipmentService.getEquipmentsOPModule(farmsAppend).subscribe((equipmentList) => {
          this.getFarmSelected().subscribe((farmIds) => {
            let responseFiltered = cloneDeep(equipmentList);
            if (farmIds?.length) {
              responseFiltered = filter(responseFiltered, (item) =>
                farmIds.includes(item.companyId)
              );
            }
            responseFiltered.forEach((equipment: Equipment) => {
              this.farmService.setFarmParamByID(equipment.companyId, 'name');
              equipment.companyName = this.farmService.getFarmByParam();
            });
            this.multiEquipmentList$.next(responseFiltered);
          });
        });
        this.fieldActivityService
          .getFieldActivitiesOPModule(farmsAppend, selectedYear)
          .subscribe((FAlist) => {
            this.getFarmSelected()
              .pipe(take(1))
              .subscribe((farmIds) => {
                this.getEmployeeSelected()
                  .pipe(take(1))
                  .subscribe((userId) => {
                    let FAlistFiltered = JSON.parse(JSON.stringify(FAlist));
                    FAlistFiltered = FAlistFiltered.filter(
                      (item) => item.cropYear === selectedYear
                    );
                    if (farmIds?.length) {
                      FAlistFiltered = FAlistFiltered.filter((item) =>
                        farmIds.includes(item.companyId)
                      );
                    }
                    if (userId) {
                      FAlistFiltered = FAlistFiltered.filter(
                        (item) => item.users.filter({ id: userId }).length > 0
                      );
                    }
                    FAlistFiltered.forEach((FAitem: FieldActivity) => {
                      this.farmService.setFarmParamByID(FAitem.companyId, 'name');
                      FAitem.companyName = this.farmService.getFarmByParam();
                    });
                    this.multiFieldActivityList$.next(FAlistFiltered);
                  });
              });
          });
        this.cropService.getCropsOPModule(farmsAppend, selectedYear).subscribe((cropList) => {
          this.getFarmSelected().subscribe((farmIds) => {
            let responseFiltered = cloneDeep(cropList);
            if (farmIds?.length) {
              // tslint:disable-next-line:max-line-length
              responseFiltered = filter(
                responseFiltered,
                (item) => farmIds.includes(item.companyId) && item.year === selectedYear
              );
            }
            responseFiltered.forEach((crop: any) => {
              this.farmService.setFarmParamByID(crop.companyId, 'name');
              crop.companyName = this.farmService.getFarmByParam();
            });
            this.multiCropList$.next(responseFiltered);
          });
        });
        this.employeeService.getEmployeesOPModule(farmsAppend).subscribe((employeeList) => {
          this.getFarmSelected().subscribe((farmIds) => {
            this.getEmployeeSelected().subscribe((userId) => {
              const responseEmployee = cloneDeep(employeeList);
              let responseFiltered = filter(responseEmployee, (item) => item.user !== undefined);
              if (farmIds?.length) {
                responseFiltered = filter(responseFiltered, (item) =>
                  farmIds.includes(item.companyId)
                );
              }
              if (userId) {
                responseFiltered = filter(responseFiltered, (item) => item.user.id === userId);
              }
              responseFiltered.forEach((employee: any) => {
                this.farmService.setFarmParamByID(employee.companyId, 'name');
                employee.companyName = this.farmService.getFarmByParam();
              });
              this.multiEmployeeList$.next(responseFiltered);
            });
          });
        });

        this.equipmentActivityService
          .getEquipmentActivitiesOPModule(farmsAppend)
          .subscribe((EAlist) => {
            this.getFarmSelected()
              .pipe(take(1))
              .subscribe((farmId) => {
                this.getEmployeeSelected()
                  .pipe(take(1))
                  .subscribe((userId) => {
                    let EAlistFiltered = cloneDeep(EAlist);
                    if (farmId?.length) {
                      EAlistFiltered = EAlistFiltered.filter((item) => item.companyId === farmId);
                    }
                    if (userId) {
                      EAlistFiltered = EAlistFiltered.filter(
                        (item) => item.people.filter((item) => item.id === userId).length > 0
                      );
                    }
                    EAlistFiltered.forEach((EAitem: any) => {
                      this.farmService.setFarmParamByID(EAitem.companyId, 'name');
                      EAitem.companyName = this.farmService.getFarmByParam();
                    });
                    this.multiEquipmentActivityList$.next(EAlistFiltered);
                  });
              });
          });

        this.noteService.getNotesOPModule(farmsAppend).subscribe((noteList) => {
          this.getFarmSelected().subscribe((farmIds) => {
            let responseFiltered = cloneDeep(noteList);
            if (farmIds?.length) {
              // tslint:disable-next-line:max-line-length
              responseFiltered = filter(responseFiltered, (item) =>
                farmIds.includes(item.companyId)
              );
            }
            responseFiltered.forEach((note: Note) => {
              const farm = farmList.find((farm: Farm) => farm.id === note.companyId);
              note.companyName = farm ? farm.name.toString() : '';
              note.user = userList.find((usr: User) => usr.id === note.user.id);
            });
            this.multiNoteList$.next(responseFiltered);
          });
        });
      }
    });
  };

  getEmployeeSelected = () => {
    return this.employeeSelected.asObservable();
  };

  updateEmployeeSelected(item: number) {
    this.employeeSelected.next(item);
  }

  getFarmSelected = () => {
    return this.farmSelected.asObservable();
  };

  updateFarmSelected(item: number[]) {
    this.farmSelected.next(item);
  }

  getDateSelected = () => {
    return this.dateSelected.asObservable();
  };

  updateDateSelected(item: number) {
    this.dateSelected.next(item);
  }
}
